import type AssetGroup from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import { captureGenericError } from './lib/sentry';
import { optimisticResources } from './loadingGroups';

/**
 * Points during launch at which an optimistic resource can start loading. Each
 * phase names what is known by then, which is all a resource may rely on.
 */
export type OptimisticPhase =
  /**
   * The engine is up; the platform may still be initializing. No entry data,
   * no Replicant state, no views.
   */
  | 'launch'
  /** Replicant state and the user's locale are known. No views yet. */
  | 'replicantReady'
  /** The first scene is loaded and launch is no longer waiting on assets. */
  | 'background'
  /**
   * The pet's own assets and its status clip are loaded, and the background
   * chain is moving on to the puzzle.
   */
  | 'petLoaded';

/**
 * Something worth downloading before the code that needs it asks for it.
 *
 * Rules, so that a resource can never make launch slower or wrong:
 *
 * 1. A resource only warms a cache: the loader's, or the browser's for files
 *    the loader does not keep. It never installs anything into game state
 *    (strings, fonts, views), so the code that needs the asset keeps loading
 *    it exactly as before and gets a cache hit or joins the request in flight.
 *    A wrong guess costs bandwidth and nothing else.
 * 2. Nothing awaits a resource. `startOptimisticLoading` returns nothing, so
 *    launch cannot block on a warm-up, and a warm-up cannot race the load it
 *    anticipates: there is no second writer.
 * 3. A warm-up whose `assets` throws or whose load rejects is reported to
 *    Sentry as `Optimistic load failed: <name>` and is otherwise ignored. A
 *    request the loader resolves as missing (404, exhausted retries, a failed
 *    decode) resolves `undefined` like any other loader request and is not
 *    reported here: the load it anticipates issues the same request and
 *    surfaces that failure itself.
 * 4. Whether there is anything to warm up is decided in `assets`, at the time
 *    the phase starts, from what that phase guarantees is known.
 */
export interface OptimisticResource {
  /** Names the resource in Sentry when its warm-up fails. */
  name: string;
  phase: OptimisticPhase;
  /** Builds the assets to warm up, or `undefined` when there are none. */
  assets: () => AssetGroup | undefined;
}

const startedPhases = new Set<OptimisticPhase>();

/**
 * Starts every optimistic resource declared for `phase`. Each phase starts
 * once; later calls are no-ops.
 */
export function startOptimisticLoading(
  phase: OptimisticPhase,
  resources: readonly OptimisticResource[] = optimisticResources,
): void {
  if (startedPhases.has(phase)) return;
  startedPhases.add(phase);

  for (const resource of resources) {
    if (resource.phase === phase) void warmUp(resource);
  }
}

async function warmUp(resource: OptimisticResource): Promise<void> {
  try {
    await resource.assets()?.load();
  } catch (error) {
    captureGenericError(`Optimistic load failed: ${resource.name}`, error);
  }
}
